import React, { useRef, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import logo from "../images/logo.png";
import {
  FaPrint, FaTimes, FaDownload, FaCut, FaFileInvoiceDollar,
} from "react-icons/fa";
import { formatPerthDate } from "../utils/perthTime";
import "../style/invoiceModal.css";

const InvoiceModal = ({ invoice, booking, onClose }) => {
  const invoiceRef = useRef(null);
  const sheetRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!invoice && !booking) return null;

  // Synthesize invoice data if only raw booking object is provided
  const buildSynthesizedData = () => {
    if (invoice) {
      const total = Number(
        invoice.total_amount ??
        invoice.total ??
        invoice.totals?.total_paid ??
        invoice.totals?.total ??
        0
      );
      const gst = Number(invoice.gst_amount ?? (total / 11).toFixed(2));
      const subtotal = Number(invoice.subtotal_ex_gst ?? (total - gst).toFixed(2));

      return {
        ...invoice,
        invoice_number: invoice.invoice_number || `INV-2026-${(invoice.booking_id || 1).toString().padStart(5, "0")}`,
        booking_id: invoice.booking_id,
        booking_ref: invoice.booking_ref || `JB-${(invoice.booking_id || 1).toString().padStart(6, "0")}`,
        invoice_date: invoice.invoice_date || invoice.created_at || new Date().toISOString().slice(0, 10),
        delivery_date: invoice.delivery_date || "",
        collection_date: invoice.collection_date || "",
        status: invoice.status || "PAID",
        business: {
          name: "Fast Australia pty ltd T/A Jerry’s Bins Perth.",
          abn: "71 629 488 820",
          email: "support@jerrysbins.com.au",
          phone: "+61 0495 000 017",
          address: "",
          website: "www.jerrysbins.com.au",
          ...(invoice.business || {}),
        },
        customer: {
          name: invoice.customer?.name || invoice.full_name || "Valued Customer",
          email: invoice.customer?.email || invoice.email || "—",
          mobile: invoice.customer?.mobile || invoice.mobile || "—",
          delivery_address: invoice.customer?.delivery_address || invoice.delivery_address || "",
        },
        line_items: invoice.line_items || [
          {
            description: `${invoice.bin_size || "Standard"} Skip Bin Hire (${invoice.waste_name || "General Waste"})`,
            quantity: 1,
            unit_price: total,
            total: total,
          },
        ],
        discount_amount: Number(invoice.discount_amount || 0),
        loyalty_discount: Number(invoice.loyalty_discount || 0),
        subtotal_ex_gst: subtotal,
        gst_amount: gst,
        total_amount: total,
        totals: {
          subtotal: subtotal,
          gst: gst,
          total: total,
          total_paid: total,
          ...(invoice.totals || {}),
        },
      };
    }
    if (!booking) return null;

    const total = Number(booking.total_amount || 0);
    const wasteCharge = Number(booking.waste_charge || 0);
    const deliveryCharge = Number(booking.delivery_charge || 0);
    const extensionFee = Number(booking.extension_fee || 0);
    const bookingFee = Number(booking.booking_fee || 0);
    const additionalCharge = Number(booking.additional_charge || 0);
    const discountAmount = Number(booking.discount_amount || 0);
    const loyaltyDiscount = Number(booking.loyalty_discount || 0);

    const extrasSum = wasteCharge + deliveryCharge + extensionFee + bookingFee + additionalCharge;
    const discountsSum = discountAmount + loyaltyDiscount;
    const binHireBase = Math.max(0, total + discountsSum - extrasSum);

    const binSize = booking.bin_size || booking.size || "Standard";
    const wasteName = booking.waste_name || "General Waste";
    const hireWeeks = Number(booking.hire_weeks || 1);

    const items = [
      {
        description: `${binSize} Skip Bin Hire (${wasteName}) - ${hireWeeks} Week${hireWeeks > 1 ? "s" : ""}`,
        quantity: 1,
        unit_price: binHireBase || total,
        total: binHireBase || total,
      },
    ];

    if (wasteCharge > 0) {
      items.push({
        description: `Waste Type Surcharge (${wasteName})`,
        quantity: 1,
        unit_price: wasteCharge,
        total: wasteCharge,
      });
    }

    if (deliveryCharge > 0) {
      items.push({
        description: "Delivery & Transport Surcharge",
        quantity: 1,
        unit_price: deliveryCharge,
        total: deliveryCharge,
      });
    }

    if (extensionFee > 0) {
      items.push({
        description: `Duration Extension Fee (${hireWeeks} weeks)`,
        quantity: 1,
        unit_price: extensionFee,
        total: extensionFee,
      });
    }

    if (bookingFee > 0) {
      items.push({
        description: "Standard Processing / Booking Fee",
        quantity: 1,
        unit_price: bookingFee,
        total: bookingFee,
      });
    }

    if (additionalCharge > 0) {
      items.push({
        description: "Additional Items & Equipment",
        quantity: 1,
        unit_price: additionalCharge,
        total: additionalCharge,
      });
    }

    const gstAmount = Number((total / 11).toFixed(2));
    const subtotalExGst = Number((total - gstAmount).toFixed(2));

    return {
      invoice_number: `INV-${(booking.created_at || "2026").toString().slice(0, 4)}-${(booking.booking_id || 1).toString().padStart(5, "0")}`,
      booking_id: booking.booking_id,
      booking_ref: booking.booking_ref || `JB-${(booking.booking_id || 1).toString().padStart(6, "0")}`,
      invoice_date: (booking.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      delivery_date: (booking.delivery_date || "").slice(0, 10),
      collection_date: (booking.collection_date || "").slice(0, 10),
      status: "PAID",
      payment_method: "PayPal / Credit Card",
      paypal_order_id: booking.paypal_order_id || "N/A",
      business: {
        name: "Fast Australia pty ltd T/A Jerry’s Bins Perth.",
        abn: "71 629 488 820",
        email: "support@jerrysbins.com.au",
        phone: "+61 0495 000 017",
        address: "",
        website: "www.jerrysbins.com.au",
      },
      customer: {
        name: booking.full_name || `${booking.first_name || ""} ${booking.last_name || ""}`.trim() || "Valued Customer",
        email: booking.email || "—",
        mobile: booking.mobile || "—",
        delivery_address: booking.delivery_address || "",
      },
      booking_summary: {
        bin_size: binSize,
        waste_type: wasteName,
        hire_weeks: hireWeeks,
      },
      line_items: items,
      discount_amount: discountAmount,
      loyalty_discount: loyaltyDiscount,
      subtotal_ex_gst: subtotalExGst,
      gst_amount: gstAmount,
      total_amount: total,
      totals: {
        subtotal: subtotalExGst,
        gst: gstAmount,
        total: total,
        total_paid: total,
      },
    };
  };

  const data = buildSynthesizedData();

  const handlePrint = () => {
    window.print();
  };

  const fmtDate = (d) => formatPerthDate(d, { day: "numeric", month: "short", year: "numeric" });

  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    let canvas = null;
    try {
      const sheet = sheetRef.current || invoiceRef.current?.querySelector(".inv-sheet");
      if (!sheet) {
        throw new Error("Invoice sheet element not found");
      }

      // Render invoice sheet to high-res canvas (scale 2 = 2x DPI)
      canvas = await html2canvas(sheet, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1000,
        windowHeight: sheet.scrollHeight + 500,
        onclone: (clonedDoc) => {
          const clonedSheet = clonedDoc.querySelector(".inv-sheet");
          if (clonedSheet) {
            clonedSheet.style.overflow = "visible";
            clonedSheet.style.maxHeight = "none";
            clonedSheet.style.height = "auto";
            clonedSheet.style.width = "780px";
            clonedSheet.style.padding = "32px 36px";
            clonedSheet.style.margin = "0";
            clonedSheet.style.boxSizing = "border-box";
          }
          const allScrollParents = clonedDoc.querySelectorAll(".inv-overlay, .inv-modal, body, html");
          allScrollParents.forEach((el) => {
            el.style.overflow = "visible";
            el.style.maxHeight = "none";
            el.style.height = "auto";
          });
          const clonedActionBar = clonedDoc.querySelector(".inv-action-bar");
          if (clonedActionBar) {
            clonedActionBar.style.display = "none";
          }
        },
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();   // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

      const margin = 10; // 10mm margins
      const maxW = pdfWidth - margin * 2;   // 190mm
      const maxH = pdfHeight - margin * 2; // 277mm

      const naturalHeight = (canvas.height * maxW) / canvas.width;

      if (naturalHeight <= maxH) {
        pdf.addImage(imgData, "PNG", margin, margin, maxW, naturalHeight, undefined, "FAST");
      } else {
        const scale = maxH / naturalHeight;
        const finalW = maxW * scale;
        const finalH = maxH;
        const xOffset = margin + (maxW - finalW) / 2;
        pdf.addImage(imgData, "PNG", xOffset, margin, finalW, finalH, undefined, "FAST");
      }

      const filename = `${data.invoice_number || "Tax_Invoice"}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error("PDF generation failed:", err);
      window.print();
    } finally {
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
        canvas = null;
      }
      setDownloading(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div
        className="inv-modal"
        ref={invoiceRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Tax Invoice Modal"
      >
        {/* MOBILE DOWNLOAD CARD (Shown on phones / small screens <= 768px) */}
        <div className="inv-mobile-card no-print">
          <div className="inv-mobile-card-header">
            <div className="inv-mobile-icon-box">
              <FaFileInvoiceDollar />
            </div>
            <div className="inv-mobile-header-text">
              <span className="inv-badge-paid">PAID TAX INVOICE</span>
              <h3 className="inv-mobile-inv-num">{data.invoice_number}</h3>
            </div>
            <button
              className="inv-btn-close-mobile"
              onClick={onClose}
              aria-label="Close modal"
              title="Close"
            >
              <FaTimes size={18} color="#ffffff" style={{ color: "#ffffff", fill: "#ffffff" }} />
            </button>
          </div>

          <div className="inv-mobile-card-body">
            <div className="inv-mobile-row">
              <span>Customer</span>
              <strong>{data.customer.name}</strong>
            </div>
            <div className="inv-mobile-row">
              <span>Reference</span>
              <strong>{data.booking_ref}</strong>
            </div>
            <div className="inv-mobile-row">
              <span>Invoice Date</span>
              <strong>{fmtDate(data.invoice_date)}</strong>
            </div>
            <div className="inv-mobile-row">
              <span>Total Amount (incl. GST)</span>
              <strong className="inv-mobile-total">${Number(data?.total_amount ?? data?.totals?.total_paid ?? data?.totals?.total ?? 0).toFixed(2)}</strong>
            </div>
          </div>

          <div className="inv-mobile-card-actions">
            <button
              className="inv-mobile-download-btn"
              onClick={handleDownloadPdf}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <span className="inv-spinner" /> Generating PDF...
                </>
              ) : (
                <>
                  <FaDownload /> Download Tax Invoice
                </>
              )}
            </button>
          </div>
        </div>

        {/* ACTION BAR (Shown on desktop > 768px, hidden during print) */}
        <div className="inv-action-bar no-print">
          <div className="inv-action-left">
            <span className="inv-badge-paid">PAID TAX INVOICE</span>
            <span className="inv-num-label">{data.invoice_number}</span>
          </div>
          <div className="inv-action-right">
            <button className="inv-btn-print" onClick={handlePrint} title="Print or Save as PDF">
              <FaPrint /> Print / Save PDF
            </button>
            <button
              className="inv-btn-download"
              onClick={handleDownloadPdf}
              disabled={downloading}
              title="Download Tax Invoice"
            >
              {downloading ? (
                <>
                  <span className="inv-spinner" /> Generating PDF...
                </>
              ) : (
                <>
                  <FaDownload /> Download PDF
                </>
              )}
            </button>
            <button
              className="inv-btn-close"
              onClick={onClose}
              aria-label="Close modal"
              title="Close modal"
            >
              <FaTimes size={16} color="#ffffff" style={{ color: "#ffffff", fill: "#ffffff", display: "block" }} />
            </button>
          </div>
        </div>

        {/* PRINTABLE INVOICE SHEET (XERO / CLASSIC AUSTRALIAN TAX INVOICE STYLE) */}
        <div className="inv-sheet inv-xero-sheet" ref={sheetRef}>
          {/* TOP THREE-COLUMN HEADER */}
          <div className="inv-xero-header">
            {/* LEFT: TAX INVOICE & CUSTOMER */}
            <div className="inv-xero-top-left">
              <h1 className="inv-xero-title">TAX INVOICE</h1>
              <div className="inv-xero-cust-block">
                <p className="inv-xero-cust-name">{data.customer.name.toUpperCase()}</p>
                {data.customer.delivery_address && (
                  <p className="inv-xero-cust-addr">{data.customer.delivery_address}</p>
                )}
                <p className="inv-xero-cust-country">AUSTRALIA</p>
              </div>
            </div>

            {/* MIDDLE: METADATA */}
            <div className="inv-xero-top-mid">
              <div className="inv-xero-meta-group">
                <span className="inv-xero-meta-lbl">Invoice Date</span>
                <span className="inv-xero-meta-val">{fmtDate(data.invoice_date)}</span>
              </div>
              <div className="inv-xero-meta-group">
                <span className="inv-xero-meta-lbl">Invoice Number</span>
                <span className="inv-xero-meta-val">{data.invoice_number}</span>
              </div>
              <div className="inv-xero-meta-group">
                <span className="inv-xero-meta-lbl">Reference</span>
                <span className="inv-xero-meta-val">{data.booking_ref}</span>
              </div>
              <div className="inv-xero-meta-group">
                <span className="inv-xero-meta-lbl">ABN</span>
                <span className="inv-xero-meta-val">{data.business.abn}</span>
              </div>
            </div>

            {/* RIGHT: LOGO & COMPANY NAME */}
            <div className="inv-xero-top-right">
              <div className="inv-xero-logo-box">
                <img src={logo} alt="Jerry's Bins" className="inv-xero-logo" />
              </div>
              <div className="inv-xero-comp-info">
                <p className="inv-xero-comp-name">{data.business.name}</p>
                <p className="inv-xero-comp-country">AUSTRALIA</p>
              </div>
            </div>
          </div>

          {/* LINE ITEMS TABLE */}
          <div className="inv-xero-table-wrap">
            <table className="inv-xero-table">
              <thead>
                <tr>
                  <th className="th-xero-desc">Description</th>
                  <th className="th-xero-qty">Quantity</th>
                  <th className="th-xero-price">Unit Price</th>
                  <th className="th-xero-amt">Amount AUD</th>
                </tr>
              </thead>
              <tbody>
                {data.line_items.map((item, idx) => (
                  <tr key={idx}>
                    <td className="td-xero-desc">{item.description}</td>
                    <td className="td-xero-qty">{(item.quantity || 1).toFixed(2)}</td>
                    <td className="td-xero-price">{Number(item.unit_price || item.total).toFixed(2)}</td>
                    <td className="td-xero-amt">{Number(item.total).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALS SUMMARY */}
          <div className="inv-xero-totals-section">
            <div className="inv-xero-totals-box">
              {data.discount_amount > 0 && (
                <div className="inv-xero-tot-row inv-xero-discount">
                  <span className="inv-xero-tot-lbl">DISCOUNT:</span>
                  <span className="inv-xero-tot-val">-{Number(data.discount_amount).toFixed(2)}</span>
                </div>
              )}
              {data.loyalty_discount > 0 && (
                <div className="inv-xero-tot-row inv-xero-loyalty">
                  <span className="inv-xero-tot-lbl">LOYALTY REWARD:</span>
                  <span className="inv-xero-tot-val">-{Number(data.loyalty_discount).toFixed(2)}</span>
                </div>
              )}
              <div className="inv-xero-tot-row inv-xero-gst-row">
                <span className="inv-xero-tot-lbl">INCLUDES GST 10%</span>
                <span className="inv-xero-tot-val">{Number(data.gst_amount).toFixed(2)}</span>
              </div>
              <div className="inv-xero-tot-row inv-xero-grand-row">
                <span className="inv-xero-tot-lbl">TOTAL AUD</span>
                <span className="inv-xero-tot-val">{Number(data.total_amount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* BOTTOM TEAR-OFF SLIP (PAYMENT ADVICE) */}
          <div className="inv-xero-advice-wrap">
            <div className="inv-xero-cut-line">
              <span className="inv-xero-scissors"><FaCut /></span>
              <div className="inv-xero-dash" />
            </div>

            <div className="inv-xero-advice-content">
              <div className="inv-xero-adv-left">
                <h2 className="inv-xero-adv-title">PAYMENT ADVICE</h2>
                <div className="inv-xero-adv-to">
                  <span className="inv-xero-adv-to-lbl">To:</span>
                  <div className="inv-xero-adv-to-addr">
                    <p className="inv-xero-adv-comp-name">{data.business.name}</p>
                    <p>AUSTRALIA</p>
                  </div>
                </div>
              </div>

              <div className="inv-xero-adv-right">
                <div className="inv-xero-adv-grid">
                  <span className="inv-adv-grid-lbl">Customer</span>
                  <span className="inv-adv-grid-val">{data.customer.name.toUpperCase()}</span>

                  <span className="inv-adv-grid-lbl">Invoice Number</span>
                  <span className="inv-adv-grid-val">{data.invoice_number}</span>

                  <span className="inv-adv-grid-lbl">Amount Due</span>
                  <span className="inv-adv-grid-val">{Number(data.total_amount).toFixed(2)}</span>

                  <span className="inv-adv-grid-lbl">Due Date</span>
                  <span className="inv-adv-grid-val">{fmtDate(data.invoice_date)}</span>

                  <span className="inv-adv-grid-lbl">Amount Enclosed</span>
                  <div className="inv-adv-enclosed-cell">
                    <div className="inv-adv-enclosed-line" />
                    <span className="inv-adv-enclosed-hint">Enter the amount you are paying above</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="inv-xero-footer-legal">
              <p>ABN: {data.business.abn}. Registered Office: {data.business.name}, Australia.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
